import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import NotFoundPage from './pages/NotFoundPage'
import TermsOfServicePage from './pages/TermsOfServicePage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import AppLayout from './components/AppLayout'
import ProtectedRoute from './components/ProtectedRoute'
import AdminProtectedRoute from './components/AdminProtectedRoute'
import HotProspectNotification from './components/HotProspectNotification'
import ErrorBoundary from './components/ErrorBoundary'
import PageLoading from './components/PageLoading'
import { ToastProvider } from './design/Toast'
import { ConfirmProvider } from './design/ConfirmDialog'

const ProspectSearchPage = lazy(() => import('./pages/ProspectSearchPage'))
const AdminEmailLimitsPage = lazy(() => import('./pages/AdminEmailLimitsPage'))
const EmailListDetailPage = lazy(() => import('./pages/EmailListDetailPage'))
const FollowUpEmailPage = lazy(() => import('./pages/FollowUpEmailPage'))
const UnifiedSettingsPage = lazy(() => import('./pages/UnifiedSettingsPage'))
const EmailServiceSettingsPage = lazy(() => import('./pages/EmailServiceSettingsPage'))
const CompanyProfilePage = lazy(() => import('./pages/CompanyProfilePage'))
const OAuthGmailCallbackPage = lazy(() => import('./pages/OAuthGmailCallbackPage'))
const OAuthLaunchGmailPage = lazy(() => import('./pages/OAuthLaunchGmailPage'))
const OAuthOutlookCallbackPage = lazy(() => import('./pages/OAuthOutlookCallbackPage'))
const LeadsHubPage = lazy(() => import('./pages/LeadsHubPage'))
const MailDeskPage = lazy(() => import('./pages/MailDeskPage'))
const FlowsPage = lazy(() => import('./pages/FlowsPage'))
const FlowDetailPage = lazy(() => import('./pages/FlowDetailPage'))
const OfficePage = lazy(() => import('./pages/OfficePage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const ReplyInboxPage = lazy(() => import('./pages/ReplyInboxPage'))
const SequenceBuilderPage = lazy(() => import('./pages/SequenceBuilderPage'))
const MailHubPage = lazy(() => import('./pages/MailHubPage'))
const SalesChatPage = lazy(() => import('./pages/SalesChatPage'))
const PhoneSettingsPage = lazy(() => import('./pages/PhoneSettingsPage'))
const OnboardingWizardPage = lazy(() => import('./pages/OnboardingWizardPage'))

/** 「連絡先探索」 is a tab of the leads screen now (LeadsHubPage), not a
 *  screen of its own. Redirected rather than dropped, and the query is carried
 *  over: the server hands out this link with a job on it
 *  （`/leads/enrichment?importJobId=…`、enrichment_import.py）, and a plain
 *  <Navigate to="/leads?tab=enrichment"> would drop the job the reader was
 *  sent to watch. */
const ToEnrichmentTab: React.FC = () => {
  const { search } = useLocation()
  const q = new URLSearchParams(search)
  q.set('tab', 'enrichment')
  return <Navigate to={`/leads?${q.toString()}`} replace />
}

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <ToastProvider>
        <ConfirmProvider>
        <div className="App">
          <HotProspectNotification />
          <Suspense fallback={<PageLoading />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/company-profile" element={<CompanyProfilePage />} />
              <Route path="/ai-lead-discovery" element={
                <ProtectedRoute>
                  <AppLayout><ProspectSearchPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/smart-email" element={
                <ProtectedRoute>
                  <Navigate to="/mail?tab=drafts" replace />
                </ProtectedRoute>
              } />
              <Route path="/send" element={
                <ProtectedRoute>
                  <Navigate to="/mail?tab=compose" replace />
                </ProtectedRoute>
              } />
              <Route path="/workflow" element={<Navigate to="/dashboard" replace />} />
              <Route path="/multi-agent" element={<Navigate to="/dashboard" replace />} />
              <Route path="/sales-workflow" element={<Navigate to="/dashboard" replace />} />
              <Route path="/profile-management" element={
                <ProtectedRoute>
                  <Navigate to="/settings" replace />
                </ProtectedRoute>
              } />
              {/* No screen of its own: the queue is three existing lists, and
                  chat is where a decision about them is actually taken. */}
              {/* Was a redirect into the chat with a canned question, which
                  is the right answer for two drafts and the wrong one for
                  twenty: a thread is a bad place to work through a pile.
                  The chat still reviews them one at a time (DraftReview). */}
              <Route path="/approvals" element={<Navigate to="/drafts" replace />} />
              {/* The mail desk. Still /drafts, and still opens on the drafts:
                  that is what most visits are for, and the link is in people's
                  history. The four tabs around the queue are the rest of the
                  same pipeline (MailDeskPage). */}
              <Route path="/drafts" element={
                <ProtectedRoute>
                  <AppLayout>
                    <MailDeskPage />
                  </AppLayout>
                </ProtectedRoute>
              } />
              {/* The flows: one row per batch of work, saying which of the
                  five stages it is in. The desk above answers the same
                  question for the whole account, which is the wrong answer
                  once there are two lists being worked at once (FlowsPage). */}
              <Route path="/flows" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FlowsPage />
                  </AppLayout>
                </ProtectedRoute>
              } />
              {/* One flow: the five stages down the page, and the companies
                  behind them — which is where 「3社がまだ診ていません」 can be
                  opened and read as three names (FlowDetailPage). */}
              <Route path="/flows/:flowId" element={
                <ProtectedRoute>
                  <AppLayout>
                    <FlowDetailPage />
                  </AppLayout>
                </ProtectedRoute>
              } />
              {/* The same five stages as people. 「どの段にいるか」 is the
                  board's answer; this one answers the question before it —
                  「いま本当に動いているのか」 — with a posture per flow, and
                  nothing on it starts work (OfficePage). */}
              <Route path="/office" element={
                <ProtectedRoute>
                  <AppLayout>
                    <OfficePage />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/prospect-search" element={
                <ProtectedRoute>
                  <Navigate to="/leads" replace />
                </ProtectedRoute>
              } />
              <Route path="/leads" element={
                <ProtectedRoute>
                  <AppLayout><LeadsHubPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/leads/search" element={
                <ProtectedRoute>
                  <Navigate to="/leads" replace />
                </ProtectedRoute>
              } />
              <Route path="/leads/enrichment" element={
                <ProtectedRoute>
                  <ToEnrichmentTab />
                </ProtectedRoute>
              } />
              <Route path="/leads/lists" element={
                <ProtectedRoute>
                  <Navigate to="/leads" replace />
                </ProtectedRoute>
              } />
              <Route path="/email-management" element={
                <ProtectedRoute>
                  <Navigate to="/mail?tab=drafts" replace />
                </ProtectedRoute>
              } />
              <Route path="/admin/email-limits" element={<Navigate to="/settings/email-limits" replace />} />
              <Route path="/settings/email-limits" element={
                <AdminProtectedRoute>
                  <AppLayout><AdminEmailLimitsPage /></AppLayout>
                </AdminProtectedRoute>
              } />
              <Route path="/email-lists" element={
                <ProtectedRoute>
                  <Navigate to="/leads" replace />
                </ProtectedRoute>
              } />
              <Route path="/email-lists/:listId" element={
                <ProtectedRoute>
                  <AppLayout><EmailListDetailPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/email-lists/:listId/send" element={
                <ProtectedRoute>
                  <AppLayout><FollowUpEmailPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <AppLayout><UnifiedSettingsPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oauth/gmail/callback" element={
                <ProtectedRoute>
                  <AppLayout><OAuthGmailCallbackPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oauth/gmail/launch" element={
                <ProtectedRoute>
                  <AppLayout><OAuthLaunchGmailPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/oauth/outlook/callback" element={
                <ProtectedRoute>
                  <AppLayout><OAuthOutlookCallbackPage /></AppLayout>
                </ProtectedRoute>
              } />
              {/* 「営業パイプライン」 was a sixth list of the same mails, cut
                  by stage — and the desk's 「送る」 tab already holds them with
                  their replies and opens (MailDeskPage). Kept as a redirect,
                  not a 404: the link is in people's history and in the open
                  notification. */}
              <Route path="/pipeline" element={
                <ProtectedRoute>
                  <Navigate to="/drafts?role=send&bucket=sent" replace />
                </ProtectedRoute>
              } />
              <Route path="/reply-inbox" element={
                <ProtectedRoute>
                  <AppLayout><ReplyInboxPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/sequences" element={
                <ProtectedRoute>
                  <AppLayout><SequenceBuilderPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <AppLayout><DashboardPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/onboarding" element={
                <ProtectedRoute>
                  <OnboardingWizardPage />
                </ProtectedRoute>
              } />
              <Route path="/chat" element={
                <ProtectedRoute>
                  <AppLayout><SalesChatPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/mail" element={
                <ProtectedRoute>
                  <AppLayout><MailHubPage /></AppLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin/email-service-settings" element={<Navigate to="/settings/email-service" replace />} />
              <Route path="/settings/email-service" element={
                <AdminProtectedRoute>
                  <AppLayout><EmailServiceSettingsPage /></AppLayout>
                </AdminProtectedRoute>
              } />
              <Route path="/settings/phone" element={
                <AdminProtectedRoute>
                  <AppLayout><PhoneSettingsPage /></AppLayout>
                </AdminProtectedRoute>
              } />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </div>
        </ConfirmProvider>
        </ToastProvider>
      </ErrorBoundary>
    </AuthProvider>
  )
}

export default App
